// Thoughts this person posted, remembered on this device only (never sent
// anywhere), so the next visit can say "someone replied to your thought".
// Each entry keeps how many replies they have already seen, and the thought's
// own key: with it (and only with it) the writer can love a reply. The server
// keeps just a hash of the key, so it cannot link thoughts to each other.
// Replies this person wrote are remembered too, to say "the writer loved your
// reply". Everything is forgotten after 90 days.
import type { Thought } from '../types';

// status: where the post is ('waiting' for a person's check, then 'live' or
// 'not_published'); text and emoji are kept only while that matters, so a
// waiting thought can still be shown to its writer.
export type PostState = 'waiting' | 'live' | 'not_published';
type Entry = { seen: number; at: number; key?: string; status?: PostState; text?: string; emoji?: string };
const KEY = 'zuzu_my_posts';
const KEEP_MS = 90 * 24 * 60 * 60 * 1000;
const MAX = 50;

function load(): Record<string, Entry> {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) || '{}') as Record<string, Entry>;
    const now = Date.now();
    for (const id of Object.keys(all)) if (!all[id] || now - all[id].at > KEEP_MS) delete all[id];
    return all;
  } catch { return {}; }
}

function save(all: Record<string, Entry>) {
  const keep = Object.entries(all).sort((a, b) => b[1].at - a[1].at).slice(0, MAX);
  try { localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(keep))); } catch { /* private mode */ }
}

/** A new secret key for a thought (32 hex characters). */
export function newPostKey(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
}

export function rememberPost(id: string, key?: string) {
  const all = load();
  all[id] = { seen: 0, at: Date.now(), key };
  save(all);
}

/** Held for a check: keep its words (only now), so it can still be shown to me. */
export function setPostWaiting(id: string, text: string, emoji?: string) {
  const all = load();
  if (!all[id]) return;
  all[id] = { ...all[id], status: 'waiting', text: text.slice(0, 2000), emoji };
  save(all);
}

export function setPostStatus(id: string, status: PostState) {
  const all = load();
  if (!all[id]) return;
  all[id].status = status;
  if (status !== 'waiting') { delete all[id].text; delete all[id].emoji; } // checked: words no longer needed
  save(all);
}

export function forgetPost(id: string) {
  const all = load();
  delete all[id];
  save(all);
}

export function postStatus(id: string): PostState | undefined {
  return load()[id]?.status;
}

/** My thoughts still waiting for a check, to show them to me (and only me). */
export function waitingPosts(): Thought[] {
  return Object.entries(load())
    .filter(([, e]) => e.status === 'waiting' && e.text)
    .map(([id, e]) => ({ id, text: e.text!, emoji: e.emoji, x: 50, y: 50, size: 18, pending: true }));
}

/** What to ask the server about: my posts that are still waiting. */
export function pendingChecks(): { thoughts: { id: string; key: string }[]; replies: string[] } {
  const thoughts = Object.entries(load()).filter(([, e]) => e.status === 'waiting' && e.key).map(([id, e]) => ({ id, key: e.key! }));
  const replies = Object.entries(loadReplies()).filter(([, e]) => e.status === 'waiting').map(([id]) => id);
  return { thoughts, replies };
}

/** The key of a thought posted from this device (it can love replies). */
export function postKey(id: string): string | undefined {
  return load()[id]?.key;
}

export function myPostIds(): Set<string> {
  return new Set(Object.keys(load()));
}

/** The newest of my thoughts with replies I have not seen yet. */
export function unseenReply(thoughts: Thought[]): { thought: Thought; count: number } | null {
  const all = load();
  let best: { thought: Thought; count: number; at: number } | null = null;
  for (const t of thoughts) {
    const e = all[t.id];
    const n = t.replies?.length ?? 0;
    if (e && n > e.seen && (!best || e.at > best.at)) best = { thought: t, count: n - e.seen, at: e.at };
  }
  return best && { thought: best.thought, count: best.count };
}

/** I have seen the replies on this thought (up to `count`). */
export function markRepliesSeen(id: string, count: number) {
  const all = load();
  if (!all[id] || all[id].seen >= count) return;
  all[id].seen = count;
  save(all);
}

// Replies this person wrote: reply id -> the thought, and whether they have
// seen that the writer loved it.
type ReplyEntry = { thoughtId: string; at: number; lovedSeen?: boolean; status?: PostState; text?: string; key?: string };
const RKEY = 'zuzu_my_replies';

function loadReplies(): Record<string, ReplyEntry> {
  try {
    const all = JSON.parse(localStorage.getItem(RKEY) || '{}') as Record<string, ReplyEntry>;
    const now = Date.now();
    for (const id of Object.keys(all)) if (!all[id] || now - all[id].at > KEEP_MS) delete all[id];
    return all;
  } catch { return {}; }
}

function saveReplies(all: Record<string, ReplyEntry>) {
  const keep = Object.entries(all).sort((a, b) => b[1].at - a[1].at).slice(0, MAX);
  try { localStorage.setItem(RKEY, JSON.stringify(Object.fromEntries(keep))); } catch { /* private mode */ }
}

export function rememberReply(replyId: string, thoughtId: string, status?: PostState, text?: string, key?: string) {
  const all = loadReplies();
  all[replyId] = { thoughtId, at: Date.now(), status, text: status === 'waiting' ? text?.slice(0, 500) : undefined, key };
  saveReplies(all);
}

/** The key of a reply written on this device: only its writer can take it back while it waits. */
export function replyKey(id: string): string | undefined {
  return loadReplies()[id]?.key;
}

export function setReplyStatus(id: string, status: PostState) {
  const all = loadReplies();
  if (!all[id]) return;
  all[id].status = status;
  if (status === 'live') delete all[id].text;
  saveReplies(all);
}

export function forgetReply(id: string) {
  const all = loadReplies();
  delete all[id];
  saveReplies(all);
}

export function replyInfo(id: string): { thoughtId: string; status?: PostState; text?: string } | undefined {
  return loadReplies()[id];
}

/** One of my replies that the thought's writer loved, not yet seen. */
export function unseenLove(thoughts: Thought[]): { thought: Thought; reply: NonNullable<Thought['replies']>[number] } | null {
  const all = loadReplies();
  for (const t of thoughts) {
    for (const r of t.replies ?? []) {
      const e = all[r.id];
      if (e && r.loved && !e.lovedSeen) return { thought: t, reply: r };
    }
  }
  return null;
}

/** I have seen the love on my replies to this thought. */
export function markLoveSeen(thought: Thought) {
  const all = loadReplies();
  let changed = false;
  for (const r of thought.replies ?? []) {
    if (all[r.id] && r.loved && !all[r.id].lovedSeen) { all[r.id].lovedSeen = true; changed = true; }
  }
  if (changed) saveReplies(all);
}
